import * as React from "react";
import { Tabs, Tab, Box, Typography, IconButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoaderForSwitchingTabs,
  setFetchedTabs,
  setSelectedTimeFrame,
  setLoaderForDateRangeSelection,
  setHighlightDatePicker,
} from "../../../../../../features/brandConfigurationReportSlice";
import PerformanceTable from "./PerformanceTable";
import { ReportType } from "../../../../../../features/brandConfigurationReportSlice";
import { BrandConfigReportEndPointLogic } from "../../../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import {
  buildColumnDefinitionsFromMetaData,
  ColumnDefinition,
  ColumnFormat,
  formatToUTC,
  formatValueToDecimal,
  getColumnLabel,
  REPORT_QUERY_ID_MAP,
  resolveEffectiveCurrencyFormat,
} from "../../../utilitiesOfBrandConfig/helperOfBrandConfig";
import { isGreaterEndDate } from "../../../../../../utils/helpers";
import dayjs from "dayjs";
import TimeFrameDateRange from "../../../utilitiesOfBrandConfig/TimeFrameDateRange";
import { useWebsiteSharedUid } from "../../../../../../customHooks/useWebsiteSharedUid";
import DownloadIcon from "@mui/icons-material/Download";
import ImportSettings from "../ImportSettings/ImportSettings";
import * as XLSX from "xlsx";

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const REPORT_TAB_MAP: Record<string, number> = {
  daily_user: 0,
  daily_brand: 1,
  monthly_brand: 2,
};

const TAB_CONFIG: Record<number, { reportType: ReportType; label: string }> = {
  0: { reportType: "daily_user", label: "Daily User Performance" },
  1: { reportType: "daily_brand", label: "Daily Brand Performance" },
  2: { reportType: "monthly_brand", label: "Monthly Brand Performance" },
};

const DAILY_MAX_DAYS = 31;
const MONTHLY_MAX_DAYS = 180;

const isMonthlyTab = (tabIndex: number) =>
  TAB_CONFIG[tabIndex]?.reportType === "monthly_brand";

const getMaxDaysForTab = (tabIndex: number) =>
  isMonthlyTab(tabIndex) ? MONTHLY_MAX_DAYS : DAILY_MAX_DAYS;

/** Returns [firstDayOfCurrentMonth, today] as dayjs objects */
const getMTDRange = (): [dayjs.Dayjs, dayjs.Dayjs] => {
  const today = dayjs();
  const firstOfMonth = today.startOf("month");
  return [firstOfMonth, today];
};

const trimToLastNDays = (
  endDate: dayjs.Dayjs,
  maxDays: number,
): [dayjs.Dayjs, dayjs.Dayjs] => {
  // subtract (maxDays - 1) so the range is inclusive of both start and end
  const newStart = endDate.subtract(maxDays - 1, "day");
  return [newStart, endDate];
};

const getDiffInDays = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): number =>
  Math.abs(endDate.diff(startDate, "day")) + 1; // inclusive

export default function DailyMonthlyPerformanceTabs() {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams("");

  const brandIdFromURL = searchParams.get("brand_id") || "";
  const reportFromURL = searchParams.get("report") || "daily_user";
  const start_date = searchParams.get("start_date") || null; // Get start_date from URL query parameters
  const end_date = searchParams.get("end_date") || null;

  const [value, setValue] = React.useState(REPORT_TAB_MAP[reportFromURL] ?? 0);
  const [userHasSelectedDates, setUserHasSelectedDates] = React.useState(false);
  const type = "site";

  // Persists "clear" across multiple tab switches until user sets new dates
  const dateActionRef = React.useRef<"set" | "clear" | null>(null);

  // Tracks the last date range fetched per tab — prevents duplicate calls
  const lastFetchedDatesPerTab = React.useRef<Record<number, string>>({});

  // Get 1st website_shared_uid from website_permissions based on current requirements
  const website_shared_uid = useWebsiteSharedUid();

  const fetchedTabs = useSelector(
    (state: any) => state.brandConfigurationReport.fetchedTabs,
  );

  const selectedTimeFrame = useSelector(
    (state: any) => state.brandConfigurationReport.selectedTimeFrame,
  );

  const overviewPerformanceData = useSelector(
    (state: any) => state.brandConfigurationReport.overviewPerformanceData,
  );

  const overviewMetaData = useSelector(
    (state: any) => state.brandConfigurationReport.overviewMetaData,
  );

  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );

  const loaderForDateRangeSelection = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForDateRangeSelection,
  );

  // if date picker is empty, Highlight date picker range when click on raw tabld id or metrics name in table
  const highlightDatePicker = useSelector(
    (state: any) => state.brandConfigurationReport.highlightDatePicker,
  );

  // Reset highlightDatePicker after 3.5 seconds
  React.useEffect(() => {
    if (!highlightDatePicker) return;
    const timer = setTimeout(() => {
      dispatch(setHighlightDatePicker(false));
    }, 3500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [highlightDatePicker]);

  // Check if both loaders are true (Disabled the TimeFrameDateRange component)
  const isDisabled = loaderForSwitchingTabs || loaderForDateRangeSelection;

  // ─── MTD default ────────────────────────────────────────────────────────────
  // Compute the MTD range once. Used as both the Redux default and the picker
  // fallback so the calendar highlights MTD even before the user picks anything.
  const mtdRange = React.useMemo(() => getMTDRange(), []);

  // What the picker actually sees — falls back to MTD when Redux has nothing
  const effectiveTimeFrame = React.useMemo((): [
    dayjs.Dayjs | null,
    dayjs.Dayjs | null,
  ] => {
    if (
      !selectedTimeFrame ||
      (Array.isArray(selectedTimeFrame) && !selectedTimeFrame[0])
    ) {
      return [null, null]; // ← empty on first load, no MTD default
    }
    return [dayjs(selectedTimeFrame[0]), dayjs(selectedTimeFrame[1])];
    // eslint-disable-next-line
  }, [selectedTimeFrame, mtdRange]);

  // ─── maxDate: prevents the user picking an end date beyond the tab limit ────
  // const maxEndDate = React.useMemo((): dayjs.Dayjs | undefined => {
  //   if (!effectiveTimeFrame?.[0]) return undefined;
  //   const startDate = dayjs(effectiveTimeFrame[0]);
  //   const maxDays = getMaxDaysForTab(value);
  //   // subtract 1 because start day itself counts as day 1 (inclusive)
  //   return startDate.add(maxDays - 1, "day");
  // }, [effectiveTimeFrame, value]);

  // ─── Sync date range into URL params ────────────────────────────────────────
  const syncDateParamsToURL = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const params = new URLSearchParams(window.location.search);
    params.set("start_date", start.format("YYYY-MM-DD"));
    params.set("end_date", end.format("YYYY-MM-DD"));
    setSearchParams(params, { replace: true });
  };

  // ─── Table headings — driven by API metadata, format driven by XLS schema ──
  const tabColumnDefinitions = React.useMemo(() => {
    const result: Record<number, ColumnDefinition[]> = {};

    Object.entries(TAB_CONFIG).forEach(([tabIndex, config]) => {
      const metaData = overviewMetaData?.[config.reportType];

      if (metaData && Object.keys(metaData).length > 0) {
        //  Primary path — use ordered, formatted metadata from API
        result[Number(tabIndex)] = buildColumnDefinitionsFromMetaData(metaData);
      } else {
        //  Fallback — derive from first data record if metadata not yet loaded
        const firstRecord = overviewPerformanceData[config.reportType]?.[0];
        result[Number(tabIndex)] = firstRecord
          ? Object.keys(firstRecord).map((key) => ({
              key,
              label: getColumnLabel(key),
              format: ColumnFormat.UNKNOWN,
              sortable: true,
            }))
          : [];
      }
    });

    return result;
  }, [overviewMetaData, overviewPerformanceData]);

  // ─── Tab switch ─────────────────────────────────────────────────────────────
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const switchingToMonthly = isMonthlyTab(newValue);
    const switchingToDaily = !isMonthlyTab(newValue);

    // ── Switching TO monthly: always clear any active date range ─────────────
    if (
      switchingToMonthly &&
      (effectiveTimeFrame?.[0] || effectiveTimeFrame?.[1])
    ) {
      setUserHasSelectedDates(false);
      dateActionRef.current = "clear";
      lastFetchedDatesPerTab.current = {};

      dispatch(setSelectedTimeFrame([null, null]));

      const params = new URLSearchParams(window.location.search);
      params.delete("start_date");
      params.delete("end_date");
      setSearchParams(params, { replace: true });
    }

    // ── Switching TO daily: trim if range exceeds 31 days ────────────────────
    if (
      switchingToDaily &&
      effectiveTimeFrame?.[0] &&
      effectiveTimeFrame?.[1]
    ) {
      const startDate = dayjs(effectiveTimeFrame[0]);
      const endDate = dayjs(effectiveTimeFrame[1]);
      const diff = getDiffInDays(startDate, endDate);

      if (diff > DAILY_MAX_DAYS) {
        const [trimmedStart, trimmedEnd] = trimToLastNDays(
          endDate,
          DAILY_MAX_DAYS,
        );
        dispatch(setSelectedTimeFrame([trimmedStart, trimmedEnd]));
        syncDateParamsToURL(trimmedStart, trimmedEnd);
        dispatch(
          setDisplayErrorSnackBar({
            message: `Date range exceeded ${DAILY_MAX_DAYS} days. Adjusted to the last ${DAILY_MAX_DAYS} days of your selection.`,
            type: "info",
          }),
        );
      }
    }
    setValue(newValue);
  };

  // ─── Date picker change ──────────────────────────────────────────────────────
  const handleDateRangeChange = (newValue: any) => {
    const [startDate, endDate] = newValue || [];

    // ── Clear ────────────────────────────────────────────────────────────────
    if (!startDate && !endDate) {
      setUserHasSelectedDates(false);
      dateActionRef.current = "clear";
      lastFetchedDatesPerTab.current = {}; //  reset all tab caches on clear

      dispatch(setSelectedTimeFrame([null, null]));

      const params = new URLSearchParams(window.location.search);
      params.delete("start_date");
      params.delete("end_date");
      setSearchParams(params, { replace: true });

      fetchPerformanceWithDates(null, null);
      return;
    }

    // ── Both dates picked ────────────────────────────────────────────────────
    if (startDate && endDate) {
      if (isGreaterEndDate({ startDate, endDate })) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Start date cannot be after end date",
            type: "error",
          }),
        );
        return;
      }

      const maxDays = getMaxDaysForTab(value);
      const diff = getDiffInDays(dayjs(startDate), dayjs(endDate));
      if (diff > maxDays) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `Date range cannot exceed ${maxDays} days for this tab`,
            type: "error",
          }),
        );
        return;
      }

      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const rangeKey = `${start.format("YYYY-MM-DD")}__${end.format("YYYY-MM-DD")}`;

      setUserHasSelectedDates(true);
      dateActionRef.current = "set";
      lastFetchedDatesPerTab.current = {}; //  reset all tab caches on new date set

      dispatch(setHighlightDatePicker(false));
      dispatch(setSelectedTimeFrame([startDate, endDate]));
      syncDateParamsToURL(start, end);

      // Mark current tab as fetched with this range immediately
      lastFetchedDatesPerTab.current[value] = rangeKey;

      fetchPerformanceWithDates(
        start.format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD"),
      );
    }
  };

  // ─── Tab data fetch ──────────────────────────────────────────────────────────
  React.useEffect(() => {
    const { reportType } = TAB_CONFIG[value];
    //const queryId = REPORT_QUERY_ID_MAP[reportType];

    const params = new URLSearchParams(window.location.search);
    params.set("report", reportType);
    setSearchParams(params, { replace: true });

    if (!brandIdFromURL) return;

    const activeStart = effectiveTimeFrame[0]
      ? effectiveTimeFrame[0].format("YYYY-MM-DD")
      : null;
    const activeEnd = effectiveTimeFrame[1]
      ? effectiveTimeFrame[1].format("YYYY-MM-DD")
      : null;

    //  Case 1: dates are active
    if (userHasSelectedDates && activeStart && activeEnd) {
      const rangeKey = `${activeStart}__${activeEnd}`;
      if (lastFetchedDatesPerTab.current[value] === rangeKey) return;
      lastFetchedDatesPerTab.current[value] = rangeKey;

      if (!fetchedTabs[reportType]) {
        fetchBrandConfigOverviewData(
          brandIdFromURL,
          reportType,
          activeStart,
          activeEnd,
        );
      } else {
        fetchPerformanceWithDates(activeStart, activeEnd);
      }
      return;
    }

    //  Case 2: dates were cleared
    if (dateActionRef.current === "clear") {
      const clearedKey = "cleared";
      if (lastFetchedDatesPerTab.current[value] === clearedKey) return;
      lastFetchedDatesPerTab.current[value] = clearedKey;

      // FIX: uninitialised tab still needs full init even in cleared state
      if (!fetchedTabs[reportType]) {
        fetchBrandConfigOverviewData(brandIdFromURL, reportType, null, null);
      } else {
        fetchPerformanceWithDates(null, null);
      }
      return;
      // Do NOT reset dateActionRef here
    }

    // Case 3: no dates ever selected — normal initial load with cache
    if (fetchedTabs[reportType]) return;
    fetchBrandConfigOverviewData(brandIdFromURL, reportType, null, null);

    // eslint-disable-next-line
  }, [value]);

  // ─── Full tab initialization (advisor + unifier + fetcher + performance) ─────
  const fetchBrandConfigOverviewData = React.useCallback(
    async (
      brandId: string,
      report: ReportType,
      startDate: string | null,
      endDate: string | null,
    ) => {
      if (!brandId?.trim() || !report) return;
      const queryId = REPORT_QUERY_ID_MAP[report];

      dispatch(setLoaderForSwitchingTabs(true));
      try {
        await Promise.all([
          BrandConfigReportEndPointLogic.getOverviewPerformance(
            oauthTokenHeader,
            report,
            queryId,
            website_shared_uid,
            type,
            brandId,
            startDate,
            endDate,
            startDate && endDate ? 0 : 10,
          ),
          BrandConfigReportEndPointLogic.getBrandOverviewAdvisorSettings(
            oauthTokenHeader,
            brandId,
            report,
          ),
        ]);

        const response =
          await BrandConfigReportEndPointLogic.getBrandOverviewUnifierSettings(
            oauthTokenHeader,
            brandId,
            report,
          );

        const rawTableIds =
          Array.isArray(response) && response.length > 0
            ? [
                ...new Set(
                  response.map((item: any) => Number(item.raw_table_id)),
                ),
              ]
            : [];

        const parsedBrandId = parseInt(brandIdFromURL, 10);
        const parsedRawTableIds = rawTableIds
          .map((id: any) => parseInt(id, 10))
          .filter((id) => !isNaN(id)); // drop any non-numeric ids
        // Guard: skip fetcher call if either value is unusable
        if (!isNaN(parsedBrandId) && parsedRawTableIds.length > 0) {
          const requestBodyForFetcher = {
            brand_id: parsedBrandId,
            raw_table_id: parsedRawTableIds,
          };
          await BrandConfigReportEndPointLogic.getBrandOverviewFetcherSettings(
            oauthTokenHeader,
            requestBodyForFetcher,
            report,
          );
        }

        dispatch(setFetchedTabs(report as any));
      } catch (error) {
        console.error(
          "Error fetching brand configuration overview data:",
          error,
        );
      } finally {
        dispatch(setLoaderForSwitchingTabs(false));
      }
    },
    // eslint-disable-next-line
    [brandIdFromURL, website_shared_uid, oauthTokenHeader],
  );

  // // ─── fetch when user selects a valid date range ─────────────────────────────
  const fetchPerformanceWithDates = React.useCallback(
    async (startDate: string | null, endDate: string | null) => {
      const { reportType } = TAB_CONFIG[value];
      const queryId = REPORT_QUERY_ID_MAP[reportType];
      if (!brandIdFromURL || !website_shared_uid) return;

      dispatch(setLoaderForDateRangeSelection(true));
      try {
        await BrandConfigReportEndPointLogic.getOverviewPerformance(
          oauthTokenHeader,
          reportType,
          queryId,
          website_shared_uid,
          type,
          brandIdFromURL,
          startDate,
          endDate,
          startDate && endDate ? 0 : 10,
        );
      } catch (err) {
        console.error("Error fetching performance data:", err);
      } finally {
        dispatch(setLoaderForDateRangeSelection(false));
      }
    },
    // eslint-disable-next-line
    [value, brandIdFromURL, website_shared_uid, oauthTokenHeader],
  );

  // ─── restore Redux state(start_date, end_date) from URL ───────────────────────────────────────────
  React.useEffect(() => {
    if (!start_date || !end_date) return;

    const start = dayjs(start_date);
    const end = dayjs(end_date);

    if (!start.isValid() || !end.isValid()) return;

    // Restore Redux state from URL
    dispatch(setSelectedTimeFrame([start, end]));

    setUserHasSelectedDates(true);
    dateActionRef.current = "set";

    const rangeKey = `${start.format("YYYY-MM-DD")}__${end.format("YYYY-MM-DD")}`;
    lastFetchedDatesPerTab.current[value] = rangeKey;

    // eslint-disable-next-line
  }, []);

  //  XLSX Download
  // When downloading the files, please download them without the currencies.(https://optival.atlassian.net/browse/GR-21070)
  const handleExportXLSX = () => {
    const { reportType } = TAB_CONFIG[value];
    const data = overviewPerformanceData?.[reportType];
    const columns = tabColumnDefinitions[value];

    if (!data?.length || !columns?.length) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "No data available to download",
          type: "warning",
        }),
      );
      return;
    }

    const formatCellValue = (
      val: any,
      col: ColumnDefinition,
      row: any,
    ): string => {
      if (val === null || val === undefined || val === "") return "";

      const effectiveFormat = resolveEffectiveCurrencyFormat(
        col.key,
        col.format,
        row,
      );

      switch (effectiveFormat) {
        case ColumnFormat.DATE:
        case ColumnFormat.DATETIME:
          return formatToUTC(String(val));

        case ColumnFormat.CURRENCY_USD:
        case ColumnFormat.CURRENCY_GBP:
        case ColumnFormat.CURRENCY_EUR: {
          const numericVal = String(val).replace(/[^0-9.-]/g, "");
          return formatValueToDecimal(numericVal);
        }

        case ColumnFormat.CURRENCY_MISSING:
          return "";

        case ColumnFormat.FLOAT:
          return formatValueToDecimal(val);

        case ColumnFormat.INT: {
          const parsed = parseInt(val, 10);
          // Guard: if value isn't purely numeric, treat it as a plain string
          return isNaN(parsed) || !/^-?\d+$/.test(String(val).trim())
            ? String(val)
            : String(parsed);
        }

        default:
          return String(val);
      }
    };

    // Build rows as plain objects with formatted values
    const sheetData = data.map((row: any) => {
      const formattedRow: Record<string, string> = {};
      columns.forEach((col) => {
        const raw = row[col.key] ?? "";
        formattedRow[col.label] = formatCellValue(raw, col, row);
      });
      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    XLSX.writeFile(
      workbook,
      `${TAB_CONFIG[value].label.replace(/\s+/g, "_")}_${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={value}
        onChange={handleChange}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          minHeight: "42px",
          "& .MuiTabs-indicator": {
            backgroundColor: isDisabled ? "#d3d3d3" : undefined,
          },
          borderBottom: isDisabled
            ? "1px solid #d3d3d3"
            : "1px solid #00000033",
          "& .MuiTab-root": {
            padding: "9px 16px",
            fontSize: "14px",
            minHeight: "42px",
          },
        }}
      >
        {Object.values(TAB_CONFIG).map((tab, index) => (
          <Tab key={index} label={tab.label} disabled={isDisabled} />
        ))}
      </Tabs>

      {Object.entries(TAB_CONFIG).map(([tabIndex, tab]) => (
        <TabPanel key={tabIndex} value={value} index={Number(tabIndex)}>
          {/* Import Settings */}
          <ImportSettings
            timeFrameSlot={
              <TimeFrameDateRange
                dataTracker="439"
                label="Time Frame"
                isDisabled={isDisabled}
                value={effectiveTimeFrame}
                onChange={handleDateRangeChange}
                isHighlighted={highlightDatePicker}
                maxDays={getMaxDaysForTab(value)}
              />
            }
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
            }}
          >
            <Box>
              <Typography variant="h6">Sample Records</Typography>

              <Typography variant="body2" color="text.secondary">
                If no date range was selected, the table will show the 10 last
                records.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "fit-content",
                marginTop: "10px",
                minWidth: "280px",
              }}
            >
              <IconButton
                color="secondary"
                size="large"
                data-tracker="374"
                sx={{ ml: "auto" }}
                disabled={
                  isDisabled ||
                  overviewPerformanceData?.[tab.reportType]?.length === 0
                }
                onClick={handleExportXLSX}
              >
                <DownloadIcon fontSize="medium" />
              </IconButton>
              {/* <TimeFrameDateRange
                dataTracker="439"
                label="Time Frame"
                isDisabled={isDisabled}
                value={effectiveTimeFrame} // MTD shown by default
                onChange={handleDateRangeChange}
              /> */}
            </Box>
          </Box>

          <PerformanceTable
            reportType={tab.reportType}
            columnDefinitions={tabColumnDefinitions[Number(tabIndex)]}
          />
        </TabPanel>
      ))}
    </Box>
  );
}
